import type { FC } from 'react';

import { RefreshCw } from 'lucide-react';
import { cn } from 'tailwind-variants';

import { GenericButton, GenericTooltip } from '@shared/components/ui/custom';
import { Skeleton } from '@shared/components/ui/skeleton';
import { formatNumber, truncateText } from '@shared/utils/formatters.utils';

const DEFAULT_ERROR_MESSAGE = 'Failed to estimate tokens.';
const INLINE_MESSAGE_MAX_LENGTH = 60;
const TOOLTIP_MESSAGE_MAX_LENGTH = 300;

export interface TokenEstimationProps {
  showSkeleton: boolean;
  tokens: number | null;
  hasError: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}

export const TokenEstimation: FC<TokenEstimationProps> = ({
  showSkeleton,
  tokens,
  hasError,
  errorMessage,
  onRetry,
}) => {
  if (hasError) {
    const message = errorMessage ?? DEFAULT_ERROR_MESSAGE;
    const isTooLongToFit = message.length > INLINE_MESSAGE_MAX_LENGTH;

    const errorLabel = (
      <span
        className={cn('text-error-foreground text-xs', isTooLongToFit && 'cursor-help')}
        data-testid="add-investigation-token-estimation-error-text"
      >
        {isTooLongToFit ? truncateText(message, INLINE_MESSAGE_MAX_LENGTH) : message}
      </span>
    );

    return (
      <div className="flex items-center gap-2 text-sm h-6" data-testid="add-investigation-token-estimation-container">
        {isTooLongToFit ? (
          <GenericTooltip
            content={truncateText(message, TOOLTIP_MESSAGE_MAX_LENGTH)}
            side="top"
            align="start"
            className="max-w-xs wrap-break-word"
          >
            {errorLabel}
          </GenericTooltip>
        ) : (
          errorLabel
        )}
        <GenericButton
          type="button"
          variant="ghost"
          size="sm"
          icon={RefreshCw}
          label="Retry"
          onClick={onRetry}
          className="text-muted-foreground hover:text-foreground h-6 px-2"
          data-testid="add-investigation-token-estimation-retry-btn"
        />
      </div>
    );
  }

  if (!showSkeleton && (!tokens || tokens <= 0)) return null;

  return (
    <div className="flex items-center gap-1.5 text-sm h-6" data-testid="add-investigation-token-estimation-container">
      <span className="text-muted-foreground">Estimated Tokens:</span>
      {showSkeleton ? (
        <Skeleton className="h-4 w-12 rounded-sm" data-testid="add-investigation-token-estimation-skeleton" />
      ) : (
        <span data-testid="add-investigation-tokens" className="font-semibold text-foreground text-base leading-none">
          {formatNumber(tokens ?? 0)}
        </span>
      )}
    </div>
  );
};
